/**
 * Promise.allSettled with a ceiling on how many requests are in flight.
 *
 * The backend rate-limits bursts: firing a page's worth of per-id requests at
 * once (Promise.all over 100+ ids) comes back as HTTP 429 for most of them, so
 * the page ends up with less data than if it had asked for less. A handful at
 * a time gets every response.
 *
 * @param {Array} items
 * @param {(item: any, index: number) => Promise<any>} worker
 * @param {object} options
 * @param {number} options.limit - requests in flight at once
 * @param {(result: object, item: any, index: number) => void} options.onSettled
 *   - called as each one lands, for progressive rendering
 * @returns {Promise<Array<{status: 'fulfilled'|'rejected', value?: any, reason?: any}>>}
 */
export const mapWithConcurrency = async (items, worker, { limit = 5, onSettled } = {}) => {
  const results = new Array(items.length);
  let next = 0;

  const run = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        results[index] = { status: 'fulfilled', value: await worker(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
      if (onSettled) onSettled(results[index], items[index], index);
    }
  };

  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, run));
  return results;
};

export default mapWithConcurrency;
